import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGameStore, PlayerColor } from '../store/gameStore';
import GameBoard from '../components/GameBoard';
import Dice from '../components/Dice';
import PlayerToken from '../components/PlayerToken';
import TriviaModal from '../components/TriviaModal';
import TruthOrDare from '../components/TruthOrDare';
import Charades from '../components/Charades';
import MoneyConfetti from '../components/MoneyConfetti';
import albumCover from '../assets/album-cover.png';
import bgImage from '../assets/bg-image.png';
import { QRCodeSVG } from 'qrcode.react';
import { supabase, Video } from '../lib/supabase';

// Community Videos Section Component
const CommunityVideosSection: React.FC<{ onPlayVideo: (video: Video) => void }> = ({ onPlayVideo }) => {
  const [showVideos, setShowVideos] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showVideos && videos.length === 0) {
      fetchVideos();
    }
  }, [showVideos]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setShowVideos(!showVideos)}
        className="w-full flex items-center justify-between text-left"
      >
        <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
          <span>🎥</span> SEE OTHERS PLAYING
        </h2>
        <span className="text-gray-400 text-sm">
          {showVideos ? '▲ Hide' : '▼ View Videos'}
        </span>
      </button>
      
      <AnimatePresence>
        {showVideos && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-3">
              {loading ? (
                <div className="text-center py-4">
                  <motion.div
                    className="text-4xl"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    ⏳
                  </motion.div>
                  <p className="text-gray-400 text-sm mt-2">Loading videos...</p>
                </div>
              ) : videos.length > 0 ? (
                videos.map((video, index) => (
                  <motion.div
                    key={video.id}
                    className="flex items-center gap-3 bg-black/30 rounded-xl p-3 hover:bg-black/50 transition-colors cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => onPlayVideo(video)}
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        '🎬'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{video.title}</p>
                      <p className="text-gray-400 text-xs">by {video.player_name}</p>
                    </div>
                    <span className="text-cyan-400 text-xl">▶</span>
                  </motion.div>
                ))
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">
                  No videos yet. Be the first to upload! 📱
                </p>
              )}
              <p className="text-gray-500 text-xs text-center pt-2">
                Record your own game and upload via QR code at the end! 📱
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Finished Phase Component with QR Code for Video Upload
const FinishedPhase: React.FC<{
  currentPlayer: any;
  players: any[];
  showMoneyConfetti: boolean;
}> = ({ currentPlayer, players, showMoneyConfetti }) => {
  const [step, setStep] = useState<'winner' | 'qr' | 'done'>('winner');
  const uploadUrl = `${window.location.origin}/upload`;

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <motion.div
          className="text-center max-w-lg w-full"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <motion.div
            className="text-8xl mb-6"
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            🎉
          </motion.div>
          
          <h1 className="text-4xl font-black text-white mb-4">
            <span className="text-cyan-400">SCAN COMPLETE!</span>
          </h1>
          
          <p className="text-gray-300 text-lg mb-6">
            Players can now upload their gameplay videos using the QR code!
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl text-black font-bold transition-colors"
            >
              🔄 Play Again
            </button>
            <button
              onClick={() => setStep('qr')}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-bold transition-colors"
            >
              ← Back
            </button>
          </div>
        </motion.div>
        <MoneyConfetti active={showMoneyConfetti} duration={8000} />
      </div>
    );
  }

  if (step === 'qr') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <motion.div
          className="text-center max-w-lg w-full"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-6xl mb-4">📱</div>
          
          <h1 className="text-3xl font-black text-white mb-2">
            <span className="text-cyan-400">SCAN TO UPLOAD</span>
          </h1>
          
          <p className="text-gray-400 text-sm mb-6">
            Scan this QR code with your phone to upload gameplay videos
          </p>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 mb-6">
            <div className="bg-white p-4 rounded-xl inline-block">
              <QRCodeSVG 
                value={uploadUrl} 
                size={200}
                bgColor="white"
                fgColor="black"
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="text-gray-400 text-xs mt-4">
              Or visit: <span className="text-cyan-400">{uploadUrl}</span>
            </p>
          </div>

          <div className="flex gap-3">
            <motion.button
              onClick={() => setStep('done')}
              className="flex-1 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl text-black font-bold transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              ✅ DONE SCANNING
            </motion.button>
          </div>

          <button
            onClick={() => setStep('winner')}
            className="mt-4 text-gray-400 hover:text-cyan-400 text-sm transition-colors"
          >
            ← Back to Winner
          </button>
        </motion.div>
        <MoneyConfetti active={showMoneyConfetti} duration={8000} />
      </div>
    );
  }

  // Default: Winner announcement
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      <motion.div
        className="text-center max-w-lg"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className="text-8xl mb-6"
          animate={{ 
            rotate: [0, 10, -10, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          👑
        </motion.div>
        
        <h1 className="text-5xl font-black text-white mb-4">
          <span className="text-yellow-400">FAME & FORTUNE!</span>
        </h1>
        
        <div className="bg-white/10 rounded-2xl p-6 mb-6">
          <p className="text-2xl text-white mb-2">
            🎉 <strong>{currentPlayer.name}</strong> WINS! 🎉
          </p>
          <p className="text-amber-300">
            Total sips consumed: {currentPlayer.sips} 🍺
          </p>
        </div>
        
        {/* All players scoreboard */}
        <div className="bg-white/5 rounded-2xl p-4 mb-6">
          <h3 className="text-lg font-bold text-white mb-3">Final Standings</h3>
          {[...players]
            .sort((a, b) => b.position - a.position)
            .map((player, index) => (
              <div key={player.id} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🎖️'}
                  </span>
                  <PlayerToken player={player} isActive={false} size="sm" />
                  <span className="text-white">{player.name}</span>
                </div>
                <div className="text-gray-400 text-sm">
                  Position: {player.position} | Sips: {player.sips}
                </div>
              </div>
            ))}
        </div>

        {/* Upload Videos CTA */}
        <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl p-4 mb-6 border border-cyan-400/30">
          <p className="text-white font-bold mb-2">📱 RECORD & SHARE YOUR GAME!</p>
          <p className="text-gray-300 text-sm mb-3">
            Did you film this game night? Scan the QR code to upload your videos!
          </p>
          <motion.button
            onClick={() => setStep('qr')}
            className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl text-black font-bold transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            📤 GET UPLOAD QR CODE
          </motion.button>
        </div>
          
        <button
          onClick={() => window.location.reload()}
          className="px-8 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-bold transition-colors"
        >
          🔄 Play Again
        </button>

        <div className="mt-4">
          <button
            onClick={() => window.location.href = '/epk'}
            className="group relative inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm tracking-wider uppercase overflow-hidden bg-gradient-to-r from-cyan-500 to-purple-500 text-black hover:from-cyan-400 hover:to-purple-400 transition-all duration-300"
          >
            <span>👑</span>
            <span>Learn More About The Artist</span>
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </motion.div>
      
      {/* Money Confetti on Winner's Page */}
      <MoneyConfetti active={showMoneyConfetti} duration={8000} />
    </div>
  );
};

const PLAYER_COLORS: PlayerColor[] = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];

const GamePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    phase,
    players,
    currentPlayerIndex,
    diceValue,
    isRolling,
    canRoll,
    currentTrivia,
    showMiniGame,
    miniGameType,
    sidebarsOpen,
    penaltyRolling,
    showMoneyConfetti,
    charadesRollingPhase,
    charadesActorId,
    charadesGuesserId,
    charadesActorRolled,
    charadesGuesserRolled,
    charadesCurrentRollerId,
    addPlayer,
    removePlayer,
    startGame,
    rollDice,
    endMiniGame,
    albumTriggerDice,
    penaltyRollDice,
    finishCharadesRolling,
  } = useGameStore();
  
  const [playerName, setPlayerName] = useState('');
  const [selectedColor, setSelectedColor] = useState<PlayerColor>('red');
  const [usedColors, setUsedColors] = useState<PlayerColor[]>([]);
  const [playingVideo, setPlayingVideo] = useState<Video | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Charades sidebar dice state
  const [charadesDiceValue, setCharadesDiceValue] = useState<number | null>(null);
  const [charadesDiceRolling, setCharadesDiceRolling] = useState(false);
  const [charadesRollingPlayer, setCharadesRollingPlayer] = useState<'actor' | 'guesser' | null>(null);

  const currentPlayer = players[currentPlayerIndex];
  
  const handleAddPlayer = () => {
    if (playerName.trim() && !usedColors.includes(selectedColor)) {
      addPlayer(playerName.trim(), selectedColor);
      setUsedColors([...usedColors, selectedColor]);
      setPlayerName('');
      // Auto-select next available color
      const nextColor = PLAYER_COLORS.find((c) => !usedColors.includes(c) && c !== selectedColor);
      if (nextColor) setSelectedColor(nextColor);
    }
  };
  
  const handleStartGame = () => {
    if (players.length >= 2) {
      startGame();
    }
  };
  
  const handleAlbumTrigger = () => {
    albumTriggerDice();
  };

  // Charades sidebar dice roll handler
  const handleCharadesDiceRoll = () => {
    if (charadesDiceRolling) return;
    if (!charadesRollingPhase) return;

    const rollingForActor = !charadesActorRolled;
    const playerId = rollingForActor ? charadesActorId : charadesGuesserId;
    if (!playerId) return;

    setCharadesDiceRolling(true);
    setCharadesRollingPlayer(rollingForActor ? 'actor' : 'guesser');

    let count = 0;
    const interval = setInterval(() => {
      count++;
      setCharadesDiceValue(Math.floor(Math.random() * 6) + 1);
      if (count >= 10) {
        clearInterval(interval);
        const finalValue = Math.floor(Math.random() * 6) + 1;
        setCharadesDiceValue(finalValue);
        setCharadesDiceRolling(false);

        finishCharadesRolling(playerId, finalValue);

        // If this was the guesser, check if game is done (win triggered or both rolled)
        if (!rollingForActor) {
          // Wait for the guesser to be truly done (may need to wait for video/trivia/game to complete)
          const waitForDone = () => {
            const state = useGameStore.getState();
            if (state.pendingWin) return; // Win already handled
            if (!state.charadesRollingPhase) return; // Already cleaned up by another handler
            if (state.charadesWaitingForVideo || state.currentTrivia || state.showMiniGame) {
              // Still waiting for video/trivia/game, check again
              setTimeout(waitForDone, 500);
              return;
            }
            // Genuinely done - clean up and advance
            setCharadesDiceValue(null);
            setCharadesRollingPlayer(null);
            useGameStore.setState({
              charadesRollingPhase: false,
              charadesActorId: null,
              charadesGuesserId: null,
              charadesActorRolled: false,
              charadesGuesserRolled: false,
              charadesCurrentRollerId: null,
              charadesWaitingForVideo: false,
              charadesPrisonPlayerId: null,
            });
            state.nextTurn();
          };
          setTimeout(waitForDone, 1500);
        } else {
          // Actor just rolled, prepare for guesser
          setTimeout(() => {
            setCharadesDiceValue(null);
          }, 1000);
        }
      }
    }, 100);
  };
  
  // Setup Phase
  if (phase === 'setup') {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        {/* Burger Menu - Links to EPK */}
        <button
          onClick={() => navigate('/')}
          className="fixed top-4 left-4 z-50 w-10 h-10 bg-black/60 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors group"
        >
          <svg className="w-5 h-5 text-white group-hover:text-cyan-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="fixed inset-0 bg-black/70 z-0" />
        <motion.div
          className="max-w-2xl w-full relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              className="inline-block mb-6"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl blur opacity-70" />
                <img
                  src={albumCover}
                  alt="Album Cover"
                  className="relative w-48 h-48 rounded-xl object-cover border-2 border-cyan-400"
                />
              </div>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl font-black text-white mb-2">
              I'M GON'T BE RICH
              <span className="block text-cyan-400 text-2xl mt-2">IF I DON'T DIE</span>
            </h1>
            <p className="text-gray-400 text-lg">The Board Game Experience</p>
            <p className="text-amber-400 text-sm mt-2">🎵 Play while listening to the album! 🎵</p>
          </div>
          
          {/* Inline Video Player - appears under title when playing */}
          <AnimatePresence>
            {playingVideo && (
              <motion.div
                className="mb-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-2xl overflow-hidden border-2 border-cyan-400">
                  <video
                    ref={videoRef}
                    src={playingVideo.video_url}
                    controls
                    autoPlay
                    className="w-full rounded-2xl"
                    onEnded={() => setPlayingVideo(null)}
                  />
                  <button
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.pause();
                      }
                      setPlayingVideo(null);
                    }}
                    className="absolute top-3 right-3 w-10 h-10 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center text-white text-xl transition-colors"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-center text-cyan-400 text-sm mt-2">{playingVideo.title} — by {playingVideo.player_name}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Rules Card */}
          <motion.div
            className="bg-white/5 backdrop-blur rounded-2xl p-6 mb-6 border border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
              <span>📋</span> HOW TO PLAY
            </h2>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">•</span>
                <span><strong className="text-cyan-400">First, listen to the whole album all the way through!</strong> 🎵</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">•</span>
                <span>After listening once, play the album again and <strong className="text-cyan-400">pause whenever the artist mentions prison, suffering, or money</strong> - press the 🎵 ALBUM TRIGGER button!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                <span>Everyone starts in <strong className="text-red-400">PRISON</strong> - roll a 6 to escape!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400">•</span>
                <span>Roll the dice = move that many spaces + take that many sips <strong className="text-amber-400">(You can only take sips after you get out of prison!)</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span><strong className="text-purple-400">Trivia spots</strong> - answer correctly to roll again!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">•</span>
                <span><strong className="text-orange-400">Game spots</strong> - Truth or Dare & Sip or Charades! 🍺</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">•</span>
                <span>First to land <strong className="text-yellow-400">exactly</strong> on <strong className="text-yellow-400">FAME &amp; FORTUNE</strong> wins! 👑 (Overshoot = stay in place!)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-400">•</span>
                <span><strong className="text-pink-400">📱 RECORD YOUR GAME!</strong> Film yourselves playing — at the end of the game, scan the QR code to upload your videos and share the fun!</span>
              </li>
            </ul>
          </motion.div>

          {/* See Others' Videos Section */}
          <motion.div
            className="bg-white/5 backdrop-blur rounded-2xl p-6 mb-6 border border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <CommunityVideosSection onPlayVideo={setPlayingVideo} />
          </motion.div>
          
          {/* Player Setup */}
          <motion.div
            className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-xl font-bold text-white mb-4">👥 ADD PLAYERS (min 2)</h2>
            
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddPlayer()}
                placeholder="Player name..."
                className="flex-1 px-4 py-3 bg-black/30 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                maxLength={15}
              />
              
              <select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value as PlayerColor)}
                className="px-4 py-3 bg-black/30 border border-white/20 rounded-xl text-white focus:outline-none focus:border-cyan-400"
                disabled={usedColors.includes(selectedColor)}
              >
                {PLAYER_COLORS.map((color) => (
                  <option key={color} value={color} disabled={usedColors.includes(color)}>
                    {color.charAt(0).toUpperCase() + color.slice(1)}
                  </option>
                ))}
              </select>
              
              <motion.button
                onClick={handleAddPlayer}
                disabled={!playerName.trim() || usedColors.includes(selectedColor)}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-xl text-black font-bold transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ADD
              </motion.button>
            </div>
            
            {/* Player list */}
            <div className="space-y-2 mb-6">
              <AnimatePresence>
                {players.map((player, index) => (
                  <motion.div
                    key={player.id}
                    className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <div className="flex items-center gap-3">
                      <PlayerToken player={player} isActive={false} size="sm" />
                      <span className="text-white font-medium">{player.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        removePlayer(player.id);
                        setUsedColors(usedColors.filter((c) => c !== player.color));
                      }}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      ✕ Remove
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {players.length === 0 && (
                <p className="text-gray-500 text-center py-4">No players added yet</p>
              )}
            </div>
            
            <motion.button
              onClick={handleStartGame}
              disabled={players.length < 2}
              className="w-full py-4 bg-gradient-to-r from-amber-400 to-orange-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-xl text-black disabled:text-gray-400 font-bold text-lg transition-all"
              whileHover={players.length >= 2 ? { scale: 1.02 } : {}}
              whileTap={players.length >= 2 ? { scale: 0.98 } : {}}
            >
              🎮 START GAME ({players.length} players)
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    );
  }
  
  // Finished Phase
  if (phase === 'finished' && currentPlayer) {
    return (
      <FinishedPhase 
        currentPlayer={currentPlayer} 
        players={players} 
        showMoneyConfetti={showMoneyConfetti} 
      />
    );
  }
  
  // Main Game Phase
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
      {/* Top Bar - Player Info */}
      <div className="fixed top-0 left-0 right-0 bg-black/80 backdrop-blur z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Burger Menu - Links to EPK */}
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 bg-white/5 border border-white/20 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors group flex-shrink-0 mr-3"
            >
              <svg className="w-5 h-5 text-white group-hover:text-cyan-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Current Player */}
            <div className="flex items-center gap-4">
              {(() => {
                // During charades rolling, show the current roller
                const displayPlayer = charadesRollingPhase && charadesCurrentRollerId
                  ? players.find(p => p.id === charadesCurrentRollerId)
                  : currentPlayer;
                if (!displayPlayer) return null;
                return (
                  <>
                    <span className="text-gray-400 text-sm">
                      {charadesRollingPhase ? '🎲 Rolling:' : 'Current Turn:'}
                    </span>
                    <PlayerToken player={displayPlayer} isActive={true} size="md" />
                    <div>
                      <p className="text-white font-bold">{displayPlayer.name}</p>
                      <p className="text-gray-400 text-sm">
                        {displayPlayer.isInPrison ? '🔒 In Prison' : `📍 Space ${displayPlayer.position}`}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Album Trigger Button */}
            <motion.button
              onClick={handleAlbumTrigger}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl text-white font-bold flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  '0 0 10px rgba(0, 255, 255, 0.3)',
                  '0 0 20px rgba(0, 255, 255, 0.6)',
                  '0 0 10px rgba(0, 255, 255, 0.3)',
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              🎵 ALBUM TRIGGER
            </motion.button>
          </div>
        </div>
      </div>
      
      {/* Main Game Area */}
      <div className="flex h-screen pt-16">
        {/* Left Sidebar - Scoreboard (animated) */}
        <AnimatePresence>
          {sidebarsOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 256, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="hidden md:block bg-black/50 backdrop-blur border-r border-white/10 overflow-hidden flex-shrink-0"
            >
              <div className="w-64 p-4 h-full overflow-y-auto">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🏆</span> SCOREBOARD
                </h3>
                
                <div className="space-y-3">
                  {[...players]
                    .sort((a, b) => b.position - a.position)
                    .map((player, index) => {
                      const isActivePlayer = charadesRollingPhase && charadesCurrentRollerId
                        ? player.id === charadesCurrentRollerId
                        : player.id === currentPlayer?.id;
                      return (
                      <motion.div
                        key={player.id}
                        className={`
                          p-3 rounded-xl
                          ${isActivePlayer
                            ? 'bg-white/10 border border-cyan-400/50'
                            : 'bg-black/30 border border-white/5'
                          }
                        `}
                        animate={isActivePlayer ? {
                          borderColor: ['rgba(34, 211, 238, 0.3)', 'rgba(34, 211, 238, 0.8)', 'rgba(34, 211, 238, 0.3)'],
                        } : {}}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''}
                          </span>
                          <PlayerToken player={player} isActive={isActivePlayer} size="sm" />
                          <span className="text-white font-medium text-sm">{player.name}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-black/30 rounded-lg px-2 py-1">
                            <span className="text-gray-500">Position</span>
                            <p className="text-white font-bold">{player.position}/30</p>
                          </div>
                          <div className="bg-black/30 rounded-lg px-2 py-1">
                            <span className="text-gray-500">Sips</span>
                            <p className="text-amber-400 font-bold">{player.sips} 🍺</p>
                          </div>
                        </div>
                        
                        {player.isInPrison && (
                          <div className="mt-2 text-xs text-red-400 flex items-center gap-1">
                            🔒 Need 6 to escape
                          </div>
                        )}
                      </motion.div>
                      );
                    })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Center - Game Board */}
        <div className="flex-1 relative bg-black overflow-auto">
          <GameBoard />
        </div>
        
        {/* Right Sidebar - Dice & Actions (animated) */}
        <AnimatePresence>
          {sidebarsOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 288, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-black/50 backdrop-blur border-l border-white/10 overflow-hidden flex-shrink-0"
            >
              <div className="w-72 p-6 h-full flex flex-col items-center justify-center">
                <motion.div
                  className="text-center mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {charadesRollingPhase ? (
                    <>
                      <h3 className="text-xl font-bold text-green-400 mb-2">🎬 CHARADES ROLL!</h3>
                      {!charadesActorRolled ? (
                        <p className="text-amber-300 text-sm">
                          {players.find(p => p.id === charadesActorId)?.name}'s turn to roll!
                        </p>
                      ) : !charadesGuesserRolled ? (
                        <p className="text-amber-300 text-sm">
                          {players.find(p => p.id === charadesGuesserId)?.name}'s turn to roll!
                        </p>
                      ) : (
                        <p className="text-green-300 text-sm">Both rolled! Moving to next turn...</p>
                      )}
                    </>
                  ) : phase === 'penalty-roll' ? (
                    <>
                      <h3 className="text-xl font-bold text-red-400 mb-2">🎲 PENALTY ROLL!</h3>
                      <p className="text-red-300 text-sm">Roll the dice to move backward!</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-white mb-2">🎲 ROLL THE DICE</h3>
                      {currentPlayer?.isInPrison && (
                        <p className="text-red-400 text-sm">Roll 6 to escape prison!</p>
                      )}
                    </>
                  )}
                </motion.div>

                {charadesRollingPhase ? (
                  <Dice
                    value={charadesDiceValue}
                    isRolling={charadesDiceRolling}
                    onRoll={handleCharadesDiceRoll}
                    disabled={charadesDiceRolling || (charadesActorRolled && charadesGuesserRolled) || !!currentTrivia || showMiniGame}
                  />
                ) : (
                  <Dice
                    value={diceValue}
                    isRolling={phase === 'penalty-roll' ? penaltyRolling : isRolling}
                    onRoll={phase === 'penalty-roll' ? penaltyRollDice : rollDice}
                    disabled={phase === 'penalty-roll' ? (!canRoll || penaltyRolling) : (!canRoll || isRolling)}
                  />
                )}
                
                {/* Mobile Scoreboard */}
                <div className="md:hidden mt-6 w-full">
                  <h4 className="text-sm font-bold text-gray-400 mb-2">SCORES</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {players.map((player) => {
                      const isActivePlayer = charadesRollingPhase && charadesCurrentRollerId
                        ? player.id === charadesCurrentRollerId
                        : player.id === currentPlayer?.id;
                      return (
                      <div
                        key={player.id}
                        className={`text-xs p-2 rounded-lg ${
                          isActivePlayer ? 'bg-white/10' : 'bg-black/30'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <PlayerToken player={player} isActive={false} size="sm" />
                          <span className="text-white truncate">{player.name}</span>
                        </div>
                        <p className="text-gray-400 mt-1">Pos: {player.position} | Sips: {player.sips}</p>
                      </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Game Legend */}
                <div className="mt-6 w-full text-xs">
                  <h4 className="font-bold text-gray-400 mb-2">LEGEND</h4>
                  <div className="space-y-1 text-gray-500">
                    <p>🔒 Prison - Start here!</p>
                    <p>❓ Trivia - Answer questions</p>
                    <p>🎮 Game - Truth or Dare & Sip / Charades</p>
                    <p>👑 Fame - WIN!</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {phase === 'trivia' && currentTrivia && <TriviaModal />}
      </AnimatePresence>
      
      <AnimatePresence>
        {showMiniGame && miniGameType === 'truth_or_dare' && (
          <TruthOrDare onComplete={endMiniGame} />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showMiniGame && miniGameType === 'charades' && (
          <Charades onComplete={endMiniGame} />
        )}
      </AnimatePresence>
      
      {/* Money Confetti */}
      <MoneyConfetti active={showMoneyConfetti} duration={6000} />
    </div>
  );
};

export default GamePage;
